import {useState, useEffect, useMemo, useRef} from "react";
import {useApi} from "../hooks/useApi";

export const useVideos = ({filteredQuery, watchHistory}) => {
  const [videos, setVideos] = useState([]);
  // Starts true so the dashboard can show a clean full-screen loader
  // until the catalog arrives, instead of flashing its chrome over an
  // empty grid.
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(filteredQuery || "");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortValue, setSortValue] = useState("in-progress");

  const {fetchVideos} = useApi();

  useEffect(() => {
    fetchVideos()
      .then(setVideos)
      .finally(() => setIsLoading(false));
  }, [fetchVideos, setVideos]);

  useEffect(() => {
    setSearchQuery(filteredQuery || "");
  }, [filteredQuery, setSearchQuery]);

  // Round-3 #6: previous attempt re-sorted whenever a *new* id appeared
  // in watchHistory. That meant the moment a user pressed Play on a
  // "Not Started" video, the card would jump out of the visible filter
  // band and the user would see it "disappear" mid-watch. The actual
  // bug Khubaib reported was that this jump happens *as soon as
  // playback starts*, not on every tick.
  //
  // Fix: snapshot each video's sort bucket and freeze it for the
  // lifetime of the current sort/filter/search/video-list. Only an
  // explicit user action (changing the sort dropdown, the category
  // filter, the search box, or the video list itself updating from
  // the API) refreshes the snapshot. Live status badges on the cards
  // still update -- only the *position in the grid* is frozen.
  const statusSnapshotRef = useRef({});

  // The intro/orientation video is delivered under the data's
  // "Orientation" category. Matched case-insensitively so a stray
  // capitalization in the catalog doesn't unpin it.
  const isOrientation = (video) =>
    (video?.category || "").toLowerCase() === "orientation";

  const getLiveStatus = (video, history) => {
    const saved = Number(history?.[video.id]?.lastPosition);
    const length = Number(video?.videoLength);
    const hasProgress = Number.isFinite(saved) && saved > 0;
    const hasValidLength = Number.isFinite(length) && length > 0;
    if (!hasProgress) return "not-started";
    if (hasValidLength && saved >= length - 2) return "completed";
    return "in-progress";
  };

  const filtered = useMemo(() => {
    const q = (searchQuery || "").toLowerCase();
    const items = videos.filter(
      (v) =>
        (v?.title || "").toLowerCase().includes(q) &&
        (selectedCategory === "All" || v?.category === selectedCategory),
    );

    // Rebuild the snapshot for the *current* filtered list using the
    // *current* watchHistory at the moment a sort/filter/search change
    // fires. The next watchHistory tick will not re-enter this branch
    // (watchHistory is intentionally NOT in deps), so card positions
    // stay stable until the user changes their view.
    const nextSnapshot = {};
    items.forEach((v) => {
      nextSnapshot[v.id] = getLiveStatus(v, watchHistory);
    });
    statusSnapshotRef.current = nextSnapshot;

    return [...items].sort((a, b) => {
      // Pin the orientation/intro video(s) to the very top of the grid,
      // ahead of the status-bucket sort. It's the program's required
      // first step, so it should be the first thing a learner sees
      // regardless of progress. Driven by the data's "Orientation"
      // category (not a hardcoded id) so additional orientation videos
      // pin automatically.
      const orientA = isOrientation(a);
      const orientB = isOrientation(b);
      if (orientA !== orientB) return orientA ? -1 : 1;

      const statusA =
        statusSnapshotRef.current[a.id] ?? getLiveStatus(a, watchHistory);
      const statusB =
        statusSnapshotRef.current[b.id] ?? getLiveStatus(b, watchHistory);
      if (statusA === sortValue && statusB !== sortValue) return -1;
      if (statusA !== sortValue && statusB === sortValue) return 1;
      return 0;
    });
    // `watchHistory` is read inside the closure but intentionally
    // omitted from deps -- see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos, searchQuery, selectedCategory, sortValue]);

  return {
    videos,
    filtered,
    isLoading,
    selectedCategory,
    sortValue,
    setSearchQuery,
    setSelectedCategory,
    setSortValue,
  };
};
